<template>
  <div class="page-wrapper">
    <div class="page-container">
      <!-- 面包屑 -->
      <nav class="breadcrumb">
        <a class="bc-link" @click.prevent="goHome">首页</a>
        <span class="bc-sep">/</span>
        <span class="bc-link">{{ config.pageTitle }}</span>
        <span class="bc-sep">/</span>
        <span class="bc-current">组方方案</span>
      </nav>

      <!-- Hero -->
      <div class="hero-row">
        <div class="hero-left">
          <p class="hero-brand">关盈堂 · 中药AI组方</p>
          <h1 class="hero-title">一人一方，关照您的健康</h1>
          <p class="hero-subtitle">
            就诊信息已带入本页，查看组方展示与剂型选项。
          </p>
        </div>
        <button class="hero-back" type="button" @click="goBack">
          <span class="hero-back-arrow" aria-hidden="true">←</span>
          <span>返回修改信息</span>
        </button>
      </div>

      <!-- 提示条 -->
      <div class="notice-bar">
        <span class="notice-icon" aria-hidden="true">!</span>
        <p class="notice-text">
          组方方案仅供专业医生审核参考，药材与剂量以最终处方为准，请勿自行用药。
        </p>
      </div>

      <!-- 双栏：左「健康档案」/ 右「组方方案」 -->
      <div class="layout">
        <aside class="side-col">
          <div class="record-card">
            <!-- <p class="record-eyebrow">YOUR HEALTH RECORD</p> -->
            <div class="record-head">
              <span class="record-avatar">{{ avatarText }}</span>
              <div class="record-meta">
                <span class="record-name">{{ consultInfo.name || "—" }}</span>
                <span class="record-sub">{{ sexText
                }}<template v-if="birthdayText">
                    · {{ birthdayText }}</template></span>
              </div>
            </div>

            <div class="record-rows">
              <div class="r-row" v-if="consultInfo.liveArea">
                <span class="r-k">常住地址</span>
                <span class="r-v">{{ consultInfo.liveArea }}</span>
              </div>
              <div class="r-row" v-if="consultInfo.birthArea">
                <span class="r-k">出生地址</span>
                <span class="r-v">{{ consultInfo.birthArea }}</span>
              </div>
              <div class="r-row" v-if="consultInfo.useDate">
                <span class="r-k">开始服药</span>
                <span class="r-v">{{ consultInfo.useDate }}</span>
              </div>
              <div class="r-row" v-if="useDaysValue">
                <span class="r-k">{{ useDaysLabel }}</span>
                <span class="r-v">{{ useDaysValue }}</span>
              </div>
              <div class="r-row" v-if="illDayValue">
                <span class="r-k">发病日期</span>
                <span class="r-v">{{ illDayValue }}</span>
              </div>
              <div class="r-row" v-if="config.showExtraBasicInfo && consultInfo.useInstructions">
                <span class="r-k">使用方法</span>
                <span class="r-v">{{ consultInfo.useInstructions }}</span>
              </div>
              <div class="r-row" v-if="config.showExtraBasicInfo && consultInfo.taboo">
                <span class="r-k">禁忌</span>
                <span class="r-v">{{ consultInfo.taboo }}</span>
              </div>
              <div class="r-row" v-if="config.showDiagnosisInfo && diagnosisType">
                <span class="r-k">诊断类型</span>
                <span class="r-v">{{ diagnosisType }}</span>
              </div>
              <div class="r-row" v-if="config.showDiagnosisInfo && diagnoseResult">
                <span class="r-k">诊断结果</span>
                <span class="r-v">{{ diagnoseResult }}</span>
              </div>
              <div class="r-row" v-if="config.showDiagnosisInfo && takeMedTime">
                <span class="r-k">服药时间</span>
                <span class="r-v">{{ takeMedTime }}</span>
              </div>
              <div class="r-row" v-if="
                !consultInfo.liveArea &&
                !consultInfo.birthArea &&
                !consultInfo.useDate
              ">
                <span class="r-k">档案</span>
                <span class="r-v">加载中…</span>
              </div>
            </div>

            <!-- 症状与就诊记录（可折叠，默认展开） -->
            <div class="record-symptoms" v-if="config.showSymptoms && hasSymptomData">
              <button type="button" class="rs-head" @click="toggleSymptomPanel"
                :aria-expanded="symptomPanelOpen ? 'true' : 'false'">
                <span class="rs-caret" :class="{ open: symptomPanelOpen }" aria-hidden="true">▾</span>
                <span class="rs-title">症状与就诊记录</span>
              </button>
              <div class="rs-body" v-show="symptomPanelOpen">
                <!-- 基本情况 / 脉象 -->
                <div class="rs-group" v-if="filteredBasicTableArray.length > 0">
                  <div class="rs-group-title">基本情况 / 脉象</div>
                  <div class="rs-grid">
                    <div class="rs-item" v-for="item in filteredBasicTableArray" :key="'b-' + item.key">
                      <span class="rs-k">{{ item.key }}</span>
                      <span class="rs-v">{{
                        formatSymptomValue(item.value)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- 三焦辨证：上/中/下焦各自可折叠 -->
                <div class="rs-group" v-if="
                  filteredShangjiaoTableArray.length > 0 ||
                  filteredZhongjiaoTableArray.length > 0 ||
                  filteredXiajiaoTableArray.length > 0
                ">
                  <div class="rs-group-title">三焦辨证</div>

                  <div class="sanjiao-block" v-if="filteredShangjiaoTableArray.length > 0">
                    <button type="button" class="sanjiao-head" @click="toggleSanjiao('shang')"
                      :aria-expanded="sanjiaoOpen.shang ? 'true' : 'false'">
                      <span class="sj-caret" :class="{ open: sanjiaoOpen.shang }" aria-hidden="true">▾</span>
                      <span class="sj-name">上焦</span>
                      <span class="sj-count">{{ filteredShangjiaoTableArray.length }} 项</span>
                    </button>
                    <div class="sanjiao-body" v-show="sanjiaoOpen.shang">
                      <div class="rs-grid">
                        <div class="rs-item" v-for="item in filteredShangjiaoTableArray" :key="'s-' + item.key">
                          <span class="rs-k">{{ item.key }}</span>
                          <span class="rs-v">{{
                            formatSymptomValue(item.value)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="sanjiao-block" v-if="filteredZhongjiaoTableArray.length > 0">
                    <button type="button" class="sanjiao-head" @click="toggleSanjiao('zhong')"
                      :aria-expanded="sanjiaoOpen.zhong ? 'true' : 'false'">
                      <span class="sj-caret" :class="{ open: sanjiaoOpen.zhong }" aria-hidden="true">▾</span>
                      <span class="sj-name">中焦</span>
                      <span class="sj-count">{{ filteredZhongjiaoTableArray.length }} 项</span>
                    </button>
                    <div class="sanjiao-body" v-show="sanjiaoOpen.zhong">
                      <div class="rs-grid">
                        <div class="rs-item" v-for="item in filteredZhongjiaoTableArray" :key="'z-' + item.key">
                          <span class="rs-k">{{ item.key }}</span>
                          <span class="rs-v">{{
                            formatSymptomValue(item.value)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="sanjiao-block" v-if="filteredXiajiaoTableArray.length > 0">
                    <button type="button" class="sanjiao-head" @click="toggleSanjiao('xia')"
                      :aria-expanded="sanjiaoOpen.xia ? 'true' : 'false'">
                      <span class="sj-caret" :class="{ open: sanjiaoOpen.xia }" aria-hidden="true">▾</span>
                      <span class="sj-name">下焦</span>
                      <span class="sj-count">{{ filteredXiajiaoTableArray.length }} 项</span>
                    </button>
                    <div class="sanjiao-body" v-show="sanjiaoOpen.xia">
                      <div class="rs-grid">
                        <div class="rs-item" v-for="item in filteredXiajiaoTableArray" :key="'x-' + item.key">
                          <span class="rs-k">{{ item.key }}</span>
                          <span class="rs-v">{{
                            formatSymptomValue(item.value)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p class="record-note">
              信息仅在本页查看中保留，尚未提交至服务端。提交前请咨询专业医生审核。
            </p>
          </div>
        </aside>
        <!-- Tab 古中医组方 内容区 -->
        <section class="main-right" v-if="!nonGroupedMode">
          <!-- 01 选择方案类型 -->
          <div class="panel-card">
            <p class="panel-eyebrow">01 / FORMULATION</p>
            <div class="panel-head">
              <h2 class="panel-title">选择方案类型</h2>
              <span class="panel-badge">剂型参考</span>
            </div>
            <div class="type-grid" v-if="groupedTabsList.length > 0">
              <button v-for="(tab, index) in groupedTabsList" :key="tab.label" type="button" class="type-card"
                :class="{ 'type-card-active': activeGroupedTab === tab.label }"
                @click="switchGroupedTab(tab.label, tab.type)">
                <span class="type-idx">{{ pad(index + 1) }}</span>
                <span class="type-name">{{ tab.label }}</span>
                <span class="type-desc">{{ tabDescription(tab.label) }}</span>
              </button>
            </div>
            <p class="panel-empty" v-else>暂无方案信息</p>
          </div>

          <!-- 02 药材组成 -->
          <div class="panel-card">
            <!-- <p class="panel-eyebrow">02 / HERBAL COMPOSITION</p> -->
            <div class="panel-head">
              <h2 class="panel-title">
                {{ currentGroup ? currentGroup.label + "方案" : "药材组成" }}
              </h2>
              <!-- <span
                class="panel-note"
                v-if="currentGroup && !currentGroup.isShangHan"
                >参考示例</span
              > -->
              <span class="panel-badge" v-if="
                activeHerbPane && !(currentGroup && currentGroup.isShangHan)
              ">{{ activeHerbPane.count }}味药材</span>
            </div>

            <template v-if="currentGroup">
              <!-- 伤寒方（特殊渲染） -->
              <div v-if="currentGroup.isShangHan" class="shanghan-info">
                <div class="shanghan-item">
                  <b>发病时间：</b>{{ groupedIllDay }}
                </div>
                <pre class="shanghan-brief">{{ shangHanText }}</pre>
              </div>

              <!-- 其他分组（月方/日方/时方/颗粒方） -->
              <template v-else>
                <el-tabs class="sub-tabs" v-model="groupedTabSubActive" v-if="currentGroup.items.length > 0">
                  <el-tab-pane v-for="(pane, index) in currentGroup.items" :key="'grp-' + index" :label="pane.title"
                    :name="index.toString()">
                    <PrescriptionTable :pane="pane" :consultInfo="consultInfo" :config="config"
                      @drug-change="drugChangeClick" @drug-weight="drugWeightClick" />
                  </el-tab-pane>
                </el-tabs>
                <p class="panel-empty" v-else>
                  暂无{{ currentGroup.label }}信息
                </p>
              </template>
            </template>

            <!-- 无任何分组数据 -->
            <p class="panel-empty" v-else>暂无处方信息</p>
          </div>
        </section>

        <!-- 组方方案（通用方 / 自拟组方） -->
        <section class="main-right" v-if="nonGroupedMode">
          <!-- 01 选择方案类型 -->
          <div class="panel-card">
            <div class="panel-head">
              <h2 class="panel-title">选择方案类型</h2>
              <span class="panel-badge">剂型参考</span>
            </div>
            <div class="type-grid" v-if="config.tabs.length > 0">
              <button v-for="(tab, index) in config.tabs" :key="tab" type="button" class="type-card"
                :class="{ 'type-card-active': activeTab === tab }" @click="switchTab(tab)">
                <span class="type-idx">{{ pad(index + 1) }}</span>
                <span class="type-name">{{ tab }}</span>
                <span class="type-desc">{{ tabDescription(tab) }}</span>
              </button>
            </div>
          </div>

          <!-- 02 组方内容 -->
          <div class="panel-card">
            <!-- <p class="panel-eyebrow">02 / {{ isInfoTab ? 'CLINICAL SUGGESTION' : 'HERBAL COMPOSITION' }}</p> -->
            <!-- <p class="panel-eyebrow">
              {{ isInfoTab ? "CLINICAL SUGGESTION" : "HERBAL COMPOSITION" }}
            </p> -->
            <div class="panel-head">
              <h2 class="panel-title">{{ activeTab }}</h2>
              <!-- <span class="panel-note" v-if="!isInfoTab && activeHerbPane"
                >参考示例</span
              > -->
              <span class="panel-badge" v-if="!isInfoTab && activeHerbPane">{{ activeHerbPane.count }}味药材</span>
            </div>

            <!-- 开方信息 -->
            <template v-if="activeTab === '开方信息'">
              <el-tabs class="sub-tabs" v-model="tabActive" v-if="tablePrescription.length > 0">
                <el-tab-pane v-for="(pane, index) in tablePrescription" :key="'pres-' + index" :label="pane.title"
                  :name="index.toString()">
                  <PrescriptionTable :pane="pane" :consultInfo="consultInfo" :config="config"
                    @drug-change="drugChangeClick" @drug-weight="drugWeightClick" />
                </el-tab-pane>
                <el-tab-pane v-if="
                  config.hasShanghanTab && shangHanPrescriptionInfos.functions
                " label="伤寒方" name="伤寒方">
                  <div class="shanghan-info">
                    <div class="shanghan-item">
                      <b>发病时间：</b>{{ illDay }}
                    </div>
                    <pre class="shanghan-brief">{{
                      shangHanPrescriptionInfos.functions.shanghan.brief
                    }}</pre>
                  </div>
                </el-tab-pane>
              </el-tabs>
              <p class="panel-empty" v-else>暂无开方信息</p>
            </template>

            <!-- 通用方信息 -->
            <template v-if="activeTab === '通用方信息'">
              <el-tabs class="sub-tabs" v-model="tabActive2" v-if="tablePrescription2.length > 0">
                <el-tab-pane v-for="(pane, index) in tablePrescription2" :key="'gen-' + index" :label="pane.title"
                  :name="index.toString()">
                  <PrescriptionTable :pane="pane" :consultInfo="consultInfo" :config="config"
                    @drug-change="drugChangeClick" @drug-weight="drugWeightClick" />
                </el-tab-pane>
              </el-tabs>
              <p class="panel-empty" v-else>暂无通用方信息</p>
            </template>

            <!-- 针灸信息 -->
            <template v-if="activeTab === '针灸信息'">
              <div class="info-block">
                <p v-for="(item, index) in resultsZJYC" :key="'zj-' + index">
                  {{ item }}
                </p>
                <p class="panel-empty" v-if="!resultsZJYC || resultsZJYC.length === 0">
                  暂无针灸信息
                </p>
              </div>
            </template>
            <template v-if="activeTab === '本草压片'">
              <div class="info-block">
                <p v-for="(item, index) in resultsZJYC" :key="'zj-' + index">
                  {{ item }}
                </p>
                <p class="panel-empty" v-if="!resultsZJYC || resultsZJYC.length === 0">
                  暂无针灸信息
                </p>
              </div>
            </template>
            <template v-if="activeTab === '本草茶饮'">
              <div class="info-block">
                <p v-for="(item, index) in resultsZJYC" :key="'zj-' + index">
                  {{ item }}
                </p>
                <p class="panel-empty" v-if="!resultsZJYC || resultsZJYC.length === 0">
                  暂无针灸信息
                </p>
              </div>
            </template>

            <!-- 健康指引信息 -->
            <template v-if="activeTab === '健康指引信息'">
              <div class="info-block">
                <p v-for="(item, index) in resultsJKYC" :key="'jk-' + index">
                  {{ item }}
                </p>
                <p class="panel-empty" v-if="!resultsJKYC || resultsJKYC.length === 0">
                  暂无健康指引信息
                </p>
              </div>
            </template>
          </div>
        </section>
      </div>

      <!-- 底部审核条 -->
      <div class="review-bar" v-if="showAiButton || showGenerateButton">
        <div class="review-text">
          <p class="review-title">让专业审核，成为下一步</p>
          <p class="review-desc">信息已核对，真实处方由专业医生审核后生成。</p>
        </div>
        <div class="review-actions">
          <button class="btn-ghost" v-if="showAiButton" type="button" @click="sendPrescriptionToAi"
            :disabled="!hasPrescriptionData">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            AI 对话草稿
          </button>
          <button class="btn-primary" v-if="showGenerateButton" type="button" @click="submit">
            生成处方
            <span class="btn-arrow" aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <!-- 返回我的服务 -->
      <div class="back-row">
        <button class="back-service" type="button" @click="goBack">
          返回我的服务 <span aria-hidden="true">→</span>
        </button>
      </div>

      <!-- 页脚 -->
      <footer class="page-footer">
        <span>专业中医服务 · 守护健康人生</span>
        <span>一人一档，让关怀更精准</span>
      </footer>
    </div>

    <!-- 更换药方弹窗 -->
    <el-dialog title="更换药方" :visible.sync="dialogDrugVisible" center destroy-on-close width="360px">
      <div class="dialog-radio-list">
        <el-radio class="dialog-radio" v-for="drug in changeDrugList" v-model="currentDrug" :label="drug" :key="drug">{{
          drug }}</el-radio>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogDrugVisible = false" size="medium">取 消</el-button>
        <el-button type="primary" @click="dialogConfirm" size="medium">修 改</el-button>
      </div>
    </el-dialog>

    <!-- 更换重量弹窗 -->
    <el-dialog title="更换重量" :visible.sync="dialogWeightVisible" center destroy-on-close width="360px">
      <div class="dialog-weight-row">
        <span class="weight-drug-name">{{
          flag == 0 ? currentItem.key : currentItem.key1
        }}</span>
        <el-input-number v-model="currentWeight" @change="numberChange" :min="1" :max="999"
          :label="flag == 0 ? currentItem.key : currentItem.key1" size="small"></el-input-number>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogWeightVisible = false" size="medium">取 消</el-button>
        <el-button type="primary" @click="dialogWeightConfirm" size="medium">修 改</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PrescriptionCard from "./PrescriptionCard.vue";
import PrescriptionTable from "./PrescriptionTable.vue";
import { EventBus } from "@/js/eventbus.js";
import utils from "@/js/utils.js";

export default {
  name: "PrescriptionPage",
  components: { PrescriptionCard, PrescriptionTable },
  props: {
    config: {
      type: Object,
      default: () => ({
        pageTitle: "处方信息",
        fetchApi: "/api/health/v2/prescription",
        relRecordApi: "/api/health/v2/rela/record",
        replaceApi: "/api/health/v1/prescription/replace",
        replaceSubmitApi: "/api/health/v1/prescription/replace/submit",
        replaceResultApi: "/api/health/v1/prescription",
        showSymptoms: false,
        showGanZhi: false,
        tabs: ["开方信息", "针灸信息", "健康指引信息"],
        showAiButtonOnAllTabs: false,
        showGenerateOnAllPrescriptionTabs: false,
        hasShanghanTab: false,
        generateRoute: "/prescriptionImage",
        showExtraBasicInfo: false,
        showDiagnosisInfo: false,
        showGranules: false,
        showMainTabCard: true,
        useGroupedApi: false,
        fetchGroupedApi: "",
        showHandmadeSummary: false,
      }),
    },
  },
  data() {
    return {
      tabActive: "0",
      tabActive2: "0",
      activeTab: "",
      activeGroupedTab: "",
      groupedTabsList: [],
      groupedIllDay: "",
      groupedTabSubActive: "0",
      prescriptions: [],
      tablePrescription: [],
      tablePrescription2: [],
      changeDrugList: [],
      shangHanPrescriptionInfos: {},
      illDay: "",
      dialogDrugVisible: false,
      dialogWeightVisible: false,
      currentDrug: "",
      currentWeight: "",
      currentItem: {},
      flag: 0,
      basicTableArray: [],
      shangjiaoTableArray: [],
      zhongjiaoTableArray: [],
      xiajiaoTableArray: [],
      consultInfo: {},
      resultsZJYC: null,
      resultsJKYC: null,
      resultsTYF: null,
      TYvisitRecordId: "",
      paySuccessImage: "/static/image/ic_pay_success.png",
      visitRecordId: "",
      patientId: "",
      symptomActiveNames: [],
      basicInfoActiveNames: [],
      diagnosisType: "",
      activeGroupedTabID: "",
      // 左侧「症状与就诊记录」默认展开
      symptomPanelOpen: true,
      // 三焦辨证：上/中/下焦分组各自默认展开
      sanjiaoOpen: { shang: true, zhong: true, xia: true },
      // 接口未返回 groups 时，自动回退为通用（扁平 prescriptionInfos）渲染
      nonGroupedMode: false,
    };
  },
  computed: {
    useDaysLabel() {
      if (!this.config.showGanZhi) return "服药天数";
      return this.consultInfo.ganZhiPrescriptionHourRange
        ? "服药时间段"
        : "服药天数";
    },
    useDaysValue() {
      if (
        this.config.showGanZhi &&
        this.consultInfo.ganZhiPrescriptionHourRange
      ) {
        return this.consultInfo.ganZhiPrescriptionHourRange;
      }
      return this.consultInfo.useDays;
    },
    currentGroup() {
      if (!this.activeGroupedTab || this.groupedTabsList.length === 0)
        return null;
      return (
        this.groupedTabsList.find((g) => g.label === this.activeGroupedTab) ||
        null
      );
    },
    // 是否使用「分组」渲染：
    // 当 config.useGroupedApi 为 true 时默认走分组；
    // 若接口未返回 groups（新版接口改为扁平返回 prescriptionInfos），会自动切换为通用渲染
    isGrouped() {
      return !!(this.config.useGroupedApi && !this.nonGroupedMode);
    },
    hasPrescriptionData() {
      if (this.isGrouped) {
        const group = this.currentGroup;
        if (!group) return false;
        return group.items && group.items.length > 0;
      }
      if (this.activeTab === "开方信息") {
        const hasRegular =
          this.tablePrescription && this.tablePrescription.length > 0;
        const hasShanghan =
          this.config.hasShanghanTab &&
          this.shangHanPrescriptionInfos &&
          this.shangHanPrescriptionInfos.functions &&
          this.shangHanPrescriptionInfos.functions.shanghan;
        if (this.tabActive === "伤寒方") return hasShanghan;
        return hasRegular;
      }
      if (this.activeTab === "通用方信息") {
        return this.tablePrescription2 && this.tablePrescription2.length > 0;
      }
      if (this.activeTab === "健康指引信息") {
        return this.resultsJKYC && this.resultsJKYC.length > 0;
      }
      if (this.activeTab === "针灸信息") {
        return this.resultsZJYC && this.resultsZJYC.length > 0;
      }
      return false;
    },
    hasShangJiao() {
      return this.shangjiaoTableArray && this.shangjiaoTableArray.length > 0;
    },
    hasZhongJiao() {
      return this.zhongjiaoTableArray && this.zhongjiaoTableArray.length > 0;
    },
    hasXiaJiao() {
      return this.xiajiaoTableArray && this.xiajiaoTableArray.length > 0;
    },
    hasSymptomData() {
      return (
        this.basicTableArray.length > 0 ||
        this.shangjiaoTableArray.length > 0 ||
        this.zhongjiaoTableArray.length > 0 ||
        this.xiajiaoTableArray.length > 0
      );
    },
    filteredBasicTableArray() {
      return this.filterSymptomItems(this.basicTableArray);
    },
    filteredShangjiaoTableArray() {
      return this.filterSymptomItems(this.shangjiaoTableArray);
    },
    filteredZhongjiaoTableArray() {
      return this.filterSymptomItems(this.zhongjiaoTableArray);
    },
    filteredXiajiaoTableArray() {
      return this.filterSymptomItems(this.xiajiaoTableArray);
    },
    symptomExpanded() {
      return (
        this.symptomActiveNames && this.symptomActiveNames.includes("symptoms")
      );
    },
    basicInfoExpanded() {
      return (
        this.basicInfoActiveNames &&
        this.basicInfoActiveNames.includes("basicInfo")
      );
    },
    showAiButton() {
      if (this.isGrouped) return true;
      if (this.config.showAiButtonOnAllTabs) return true;
      return (
        this.activeTab === "开方信息" ||
        this.activeTab === "通用方信息" ||
        this.activeTab === "健康指引信息" ||
        this.activeTab === "针灸信息"
      );
    },
    showGenerateButton() {
      if (this.isGrouped) {
        // 伤寒方不支持生成处方
        const group = this.currentGroup;
        if (group && group.isShangHan) return false;
        return true;
      }
      if (this.config.showGenerateOnAllPrescriptionTabs) {
        return this.activeTab === "开方信息" || this.activeTab === "通用方信息";
      }
      return this.activeTab === "开方信息";
    },
    diagnoseResult() {
      if (this.isGrouped && this.activeGroupedTab) {
        const group = this.currentGroup;
        if (
          group &&
          !group.isShangHan &&
          group.items &&
          group.items.length > 0
        ) {
          return group.items[0].disease || "";
        }
        return "";
      }
      if (this.tablePrescription && this.tablePrescription.length > 0) {
        return this.tablePrescription[0].disease || "";
      }
      return "";
    },
    takeMedTime() {
      if (this.isGrouped && this.activeGroupedTab) {
        const group = this.currentGroup;
        if (
          group &&
          !group.isShangHan &&
          group.items &&
          group.items.length > 0
        ) {
          return group.items[0].takeTime || "";
        }
        return "";
      }
      if (this.tablePrescription && this.tablePrescription.length > 0) {
        return this.tablePrescription[0].takeTime || "";
      }
      return "";
    },
    shangHanText() {
      if (!this.isGrouped) return "";
      const group = this.currentGroup;
      if (!group || !group.isShangHan || !group.items || !group.items[0])
        return "";
      const sh = group.items[0];
      // 兼容两种结构：functions.prescription 或 functions.shanghan
      const content =
        (sh.functions &&
          (sh.functions.prescription || sh.functions.shanghan)) ||
        sh;
      return content.brief || content.text || JSON.stringify(content, null, 2);
    },
    /* ======== 新版式：左侧健康档案 ======== */
    avatarText() {
      const name =
        this.consultInfo && this.consultInfo.name
          ? String(this.consultInfo.name)
          : "";
      return name ? name.slice(0, 1) : "患";
    },
    sexText() {
      if (this.isEmptyValue(this.consultInfo && this.consultInfo.sex))
        return "";
      return String(this.consultInfo.sex) === "1" ? "男" : "女";
    },
    birthdayText() {
      const info = this.consultInfo || {};
      if (!info.birthday) return "";
      let text = info.birthday;
      // 出生时分 暂时隐藏（保留代码，恢复时去掉注释即可）
      // if (this.shouldShowTime(info.birthHour, info.birthMinute)) {
      //   text += ` ${this.formatTimeValue(info.birthHour)}:${this.formatTimeValue(info.birthMinute)}`
      // }
      return text;
    },
    illDayValue() {
      return this.illDay || this.groupedIllDay || "";
    },
    /* ======== 新版式：右侧组方方案 ======== */
    isInfoTab() {
      return this.activeTab === "针灸信息" || this.activeTab === "健康指引信息";
    },
    // 当前展示的方案明细（用于「N 味药材」徽标与计数）
    activeHerbPane() {
      if (this.isGrouped) {
        const group = this.currentGroup;
        if (
          !group ||
          group.isShangHan ||
          !group.items ||
          group.items.length === 0
        )
          return null;
        return (
          group.items[parseInt(this.groupedTabSubActive)] ||
          group.items[0] ||
          null
        );
      }
      if (this.activeTab === "开方信息") {
        return (
          this.tablePrescription[parseInt(this.tabActive)] ||
          this.tablePrescription[0] ||
          null
        );
      }
      if (this.activeTab === "通用方信息") {
        return (
          this.tablePrescription2[parseInt(this.tabActive2)] ||
          this.tablePrescription2[0] ||
          null
        );
      }
      return null;
    },
  },
  created() {
    // showMainTabCard === false 时优先按分组渲染；若接口无 groups 会自动回退为通用渲染
    this.nonGroupedMode = this.config.showMainTabCard !== false;
    this.activeTab = this.config.tabs[0] || "开方信息";
    if (this.isGrouped) {
      this.activeGroupedTab = "";
    }
    this.visitRecordId = this.$route.query.visitRecordId;
    this.patientId = this.$route.query.patientId;
    this.fetchPrescriptionData();
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.changeType(tab);
    },
    shouldShowTime(hour, minute) {
      return !(this.isEmptyValue(hour) && this.isEmptyValue(minute));
    },
    isEmptyValue(value) {
      return value === "" || value === null || value === undefined;
    },
    formatTimeValue(value) {
      if (value === "-1" || value === -1 || value === "--") return "--";
      return value;
    },
    filterSymptomItems(arr) {
      if (!arr || arr.length === 0) return [];
      return arr.filter((item) => {
        if (!item || !item.value) return false;
        const raw = Array.isArray(item.value)
          ? item.value.join("，")
          : String(item.value);
        const val = raw.trim();
        if (!val) return false;
        if (val === "正常" || val === "无") return false;
        return true;
      });
    },
    formatSymptomValue(value) {
      if (Array.isArray(value)) value = value.join("，");
      return String(value).replace(/,/g, "，");
    },
    goBack() {
      this.$router.go(-1);
    },
    goHome() {
      this.$router.push("/");
    },
    pad(n) {
      const s = String(n);
      return s.length < 2 ? "0" + s : s;
    },
    toggleSymptomPanel() {
      this.symptomPanelOpen = !this.symptomPanelOpen;
    },
    toggleSanjiao(key) {
      const next = Object.assign({}, this.sanjiaoOpen);
      next[key] = !next[key];
      this.sanjiaoOpen = next;
    },
    tabDescription(label) {
      const map = {
        月方: "传统饮片 · 煎煮服用",
        月饮片: "传统饮片 · 煎煮服用",
        日方: "每日方 · 随证加减",
        时方: "时令方 · 按时调养",
        颗粒方: "颗粒剂 · 冲服",
        月颗粒: "配方颗粒 · 冲服",
        伤寒方: "经典经方 · 辨证参考",
        开方信息: "组方药材明细",
        通用方信息: "通用方药材明细",
        针灸信息: "穴位与调理建议",
        健康指引信息: "健康趋势与调养建议",
      };
      return map[label] || "辨证组方参考";
    },

    /* ======== 分组接口相关（古中医组方） ======== */

    generateGroupedTableData(data) {
      this.groupedTabsList = [];
      this.groupedIllDay = data.illDay || "";

      if (
        !data.groups ||
        !Array.isArray(data.groups) ||
        data.groups.length === 0
      ) {
        this.activeGroupedTab = "";
        return;
      }

      for (const group of data.groups) {
        if (group.label === "伤寒方") {
          // 伤寒方：保持原始 JSON 透传
          if (group.list && group.list.length > 0) {
            this.groupedTabsList.push({
              label: "伤寒方",
              items: group.list,
              isShangHan: true,
            });
          }
        } else {
          // 月方/日方/时方/颗粒方：处理 PrescriptionInfo
          const panes = [];
          for (const item of group.list) {
            const row = {
              isHandmade: !!this.config.showHandmadeSummary,
              count: item.count,
              charge: item.charge,
              totalWeight: item.totalWeight,
              decoctionTotalWeight: item.decoctionTotalWeight,
              decoctionPrice: item.decoctionPrice,
              startDate: item.startDate,
              endDate: item.endDate,
              supplyDays: item.supplyDays,
              title: item.name,
              granulesTotalWeight: item.granulesTotalWeight,
              granulesSumPrice: item.granulesSumPrice,
              takeTime: item.takeTime,
              disease: item.disease,
            };
            const itemArray = (item.prescription || []).map((drug) =>
              Object.assign({}, drug)
            );
            const options = [];
            for (let k = 0; k < itemArray.length; k += 2) {
              const left = itemArray[k] || {};
              const right = itemArray[k + 1];
              const pair = Object.assign({}, left);
              if (right && right.key != null && right.key !== "") {
                pair.key1 = right.key;
                pair.value1 = right.value;
                pair.index1 = right.index;
              }
              options.push(pair);
            }
            row.options = options;
            panes.push(row);
          }
          if (panes.length > 0) {
            this.groupedTabsList.push({
              label: group.label,
              type: group.type,
              items: panes,
              isShangHan: false,
            });
          }
        }
      }

      // 设置默认激活 tab
      if (this.groupedTabsList.length > 0) {
        this.activeGroupedTab = this.groupedTabsList[0].label;
        this.activeGroupedTabID = this.groupedTabsList[0].type;
      }
    },

    switchGroupedTab(label, type) {
      this.activeGroupedTab = label;
      this.activeGroupedTabID = type;
      console.log(this.activeGroupedTabID);
      this.groupedTabSubActive = "0";
    },

    // 通用（扁平 prescriptionInfos / commonPrescription）数据解析
    // 分组接口与通用接口共用：两侧返回结构一致时都可复用
    applyPlainPrescriptionData(payload) {
      const data = payload || {};
      this.consultInfo = data.patient || {};
      if (this.config.hasShanghanTab && data.shangHanPrescriptionInfos) {
        this.shangHanPrescriptionInfos = data.shangHanPrescriptionInfos;
        this.illDay = data.illDay;
      }
      // 开始服药日期 / 服药天数：优先取顶层字段，缺失时回退到首个处方
      const first =
        data.prescriptionInfos && data.prescriptionInfos.length > 0
          ? data.prescriptionInfos[0]
          : data.commonPrescription || null;
      this.consultInfo.useDate =
        data.startDate || (first ? first.startDate : "");
      this.consultInfo.useDays =
        data.takeDays || (first ? first.supplyDays || first.times : "");
      if (this.config.showGanZhi) {
        this.consultInfo.ganZhiPrescriptionTimeType =
          data.ganZhiPrescriptionTimeType;
        this.consultInfo.ganZhiPrescriptionHourRange = data.takeHours;
      }
      if (this.config.showExtraBasicInfo) {
        this.consultInfo.useInstructions = data.useInstructions;
        this.consultInfo.taboo = data.taboo;
      }
      if (this.config.showDiagnosisInfo) {
        this.diagnosisType = data.diagnosisType;
      }
      if (this.config.showSymptoms && data.inquiryInfo) {
        this.basicTableArray = [].concat(
          data.inquiryInfo.basic_array || [],
          data.inquiryInfo.basic_enter_array || [],
          data.inquiryInfo.maixiang_array || []
        );
        this.shangjiaoTableArray = data.inquiryInfo.shangjiao_array || [];
        this.zhongjiaoTableArray = data.inquiryInfo.zhongjiao_array || [];
        this.xiajiaoTableArray = data.inquiryInfo.xiajiao_array || [];
      }
      // 兼容新旧数据格式：
      // 旧格式：data.prescriptionInfos = [{prescription, count, ...}]
      // 新格式（commonPrescription 接口）：data.prescriptionInfos = []，实际数据在 data.commonPrescription
      this.prescriptions =
        data.prescriptionInfos && data.prescriptionInfos.length > 0
          ? data.prescriptionInfos
          : data.commonPrescription
            ? [data.commonPrescription]
            : [];
      this.generateTableData();
    },

    fetchPrescriptionData() {
      // 分组接口路径（古中医组方）
      if (this.isGrouped && this.config.fetchGroupedApi) {
        this.wjtHttp.post(
          this,
          this.config.fetchGroupedApi,
          { visitRecordId: this.visitRecordId },
          false,
          (res) => {
            const data = res.data;
            if (data.code == 200) {
              const payload = data.data || {};
              const hasGroups =
                Array.isArray(payload.groups) && payload.groups.length > 0;
              // 新版接口不再返回 groups，而是扁平返回 prescriptionInfos / commonPrescription，自动回退为通用渲染
              if (
                !hasGroups &&
                (payload.prescriptionInfos || payload.commonPrescription)
              ) {
                this.nonGroupedMode = true;
                this.applyPlainPrescriptionData(payload);
                return;
              }
              this.consultInfo = payload.patient || {};
              this.consultInfo.useDate = payload.startDate;
              this.consultInfo.useDays = payload.takeDays;
              if (this.config.showGanZhi) {
                this.consultInfo.ganZhiPrescriptionTimeType =
                  payload.ganZhiPrescriptionTimeType;
                this.consultInfo.ganZhiPrescriptionHourRange =
                  payload.takeHours;
              }
              if (this.config.showSymptoms && payload.inquiryInfo) {
                this.basicTableArray = [].concat(
                  payload.inquiryInfo.basic_array || [],
                  payload.inquiryInfo.basic_enter_array || [],
                  payload.inquiryInfo.maixiang_array || []
                );
                this.shangjiaoTableArray =
                  payload.inquiryInfo.shangjiao_array || [];
                this.zhongjiaoTableArray =
                  payload.inquiryInfo.zhongjiao_array || [];
                this.xiajiaoTableArray =
                  payload.inquiryInfo.xiajiao_array || [];
              }
              this.generateGroupedTableData(payload);
            } else {
              this.$message({
                message:
                  data.msg && data.msg.length > 0
                    ? data.msg
                    : "出方异常，请联系管理员",
                type: "error",
              });
            }
          },
          (err) => {
            console.log(err);
          }
        );
        return;
      }

      // 通用接口路径（自拟组方/通用方/新版处方接口）
      this.wjtHttp.post(
        this,
        this.config.fetchApi,
        { visitRecordId: this.visitRecordId },
        false,
        (res) => {
          const data = res.data;
          if (data.code == 200) {
            this.applyPlainPrescriptionData(data.data || {});
          } else {
            this.$message({
              message:
                data.msg && data.msg.length > 0
                  ? data.msg
                  : "出方异常，请联系管理员",
              type: "error",
            });
          }
        },
        (err) => {
          console.log(err);
        }
      );
    },

    changeType(tab) {
      if (tab == "针灸信息" && !this.resultsZJYC) this.getRecord("A");
      else if (tab == "健康指引信息" && !this.resultsJKYC) this.getRecord("D");
      else if (tab == "通用方信息" && !this.resultsTYF) this.getRecord("G");
      else if (tab == "本草茶饮" && !this.resultsTYF) this.getRecord("T");
      else if (tab == "本草压片" && !this.resultsTYF) this.getRecord("C");
    },

    getRecord(type) {
      const params = {
        relaVisitRecordId: this.visitRecordId,
        patientId: this.patientId,
        recordType: type,
        toDoDay: this.consultInfo.useDate,
        takeDays: this.consultInfo.useDays,
      };
      this.wjtHttp.post(
        this,
        this.config.relRecordApi,
        params,
        false,
        (res) => {
          const data = res.data;
          if (data.code == 200) {
            if (this.activeTab == "通用方信息") {
              // 兼容新旧数据格式
              this.resultsTYF =
                data.data.prescriptionInfos &&
                  data.data.prescriptionInfos.length > 0
                  ? data.data.prescriptionInfos
                  : data.data.commonPrescription
                    ? [data.data.commonPrescription]
                    : [];
              this.TYvisitRecordId = data.data.visitRecordId;
              this.generateTableData2();
              return;
            }
            const rs = data.data.split("\n");
            const newRS = [];
            rs.forEach((val) => {
              if (val.length != 0) newRS.push(val.replace(/ /g, ""));
            });
            if (this.activeTab == "针灸信息") this.resultsZJYC = newRS;
            else if (this.activeTab == "健康指引信息") this.resultsJKYC = newRS;
            else if (this.activeTab == "本草茶饮") this.resultsZJYC = newRS;
            else if (this.activeTab == "本草压片") this.resultsZJYC = newRS;
          } else {
            this.$message({
              message:
                data.msg && data.msg.length > 0
                  ? data.msg
                  : "出方异常，请联系管理员",
              type: "error",
            });
          }
        },
        (err) => {
          console.log(err);
        }
      );
    },

    generateTableData() {
      this.tablePrescription = [];
      for (let i = 0; i < this.prescriptions.length; i++) {
        const item = this.prescriptions[i];
        const row = {
          isHandmade: !!this.config.showHandmadeSummary,
          count: item.count,
          charge: item.charge,
          totalWeight: item.totalWeight,
          decoctionTotalWeight: item.decoctionTotalWeight,
          decoctionPrice: item.decoctionPrice,
          startDate: item.startDate,
          endDate: item.endDate,
          supplyDays: item.supplyDays,
          title: item.name,
          granulesTotalWeight: item.granulesTotalWeight,
          granulesSumPrice: item.granulesSumPrice,
          takeTime: item.takeTime,
          disease: item.disease,
        };
        // 深拷贝处方数组，避免修改原始接口数据，并两两配对成一行
        const itemArray = (item.prescription || []).map((drug) =>
          Object.assign({}, drug)
        );
        const options = [];
        for (let k = 0; k < itemArray.length; k += 2) {
          const left = itemArray[k] || {};
          const right = itemArray[k + 1];
          const pair = Object.assign({}, left);
          if (right && right.key != null && right.key !== "") {
            pair.key1 = right.key;
            pair.value1 = right.value;
            pair.index1 = right.index;
          }
          options.push(pair);
        }
        row.options = options;
        this.tablePrescription.push(row);
      }
    },

    generateTableData2() {
      if (!this.resultsTYF) return;
      this.tablePrescription2 = [];
      for (let i = 0; i < this.resultsTYF.length; i++) {
        const item = this.resultsTYF[i];
        const row = {
          count: item.count,
          charge: item.charge,
          totalWeight: item.totalWeight,
          startDate: item.startDate,
          endDate: item.endDate,
          supplyDays: item.supplyDays,
          title: item.name,
        };
        const itemArray = (item.prescription || []).map((drug) =>
          Object.assign({}, drug)
        );
        const options = [];
        for (let k = 0; k < itemArray.length; k += 2) {
          const left = itemArray[k] || {};
          const right = itemArray[k + 1];
          const pair = Object.assign({}, left);
          if (right && right.key != null && right.key !== "") {
            pair.key1 = right.key;
            pair.value1 = right.value;
            pair.index1 = right.index;
          }
          options.push(pair);
        }
        row.options = options;
        this.tablePrescription2.push(row);
      }
    },

    submit() {
      sessionStorage.setItem("consultInfo", JSON.stringify(this.consultInfo));
      if (this.config.showSymptoms) {
        sessionStorage.setItem(
          "basicTable",
          JSON.stringify(this.basicTableArray)
        );
        sessionStorage.setItem(
          "shangjiaoTableArray",
          JSON.stringify(this.shangjiaoTableArray)
        );
        sessionStorage.setItem(
          "zhongjiaoTableArray",
          JSON.stringify(this.zhongjiaoTableArray)
        );
        sessionStorage.setItem(
          "xiajiaoTableArray",
          JSON.stringify(this.xiajiaoTableArray)
        );
      }
      if (this.isGrouped) {
        // 古中医组方：保存当前分组数据
        const group = this.currentGroup;
        const panes = group && !group.isShangHan ? group.items || [] : [];
        sessionStorage.setItem("tablePrescription", JSON.stringify(panes));
        this.$router.push({
          path: this.config.generateRoute,
          query: {
            visitRecordId: this.visitRecordId,
            prescriptionType: this.activeGroupedTabID,
          },
        });
        return;
      }
      if (this.activeTab == "通用方信息") {
        sessionStorage.setItem(
          "tablePrescription",
          JSON.stringify(this.tablePrescription2)
        );
        this.$router.push({
          path: this.config.generateRoute,
          query: { visitRecordId: this.TYvisitRecordId },
        });
      } else {
        sessionStorage.setItem(
          "tablePrescription",
          JSON.stringify(this.tablePrescription)
        );
        this.$router.push({
          path: this.config.generateRoute,
          query: { visitRecordId: this.visitRecordId },
        });
      }
    },

    drugChangeClick(item, flag) {
      this.currentItem = item;
      this.flag = flag;
      const subIdx = this.isGrouped
        ? parseInt(this.groupedTabSubActive)
        : parseInt(this.tabActive);
      const params = {
        patientId: this.patientId,
        visitRecordId: this.visitRecordId,
        medicineName: flag == 0 ? item.key : item.key1,
        number: subIdx + 1,
      };
      this.wjtHttp.post(
        this,
        this.config.replaceApi,
        params,
        false,
        (res) => {
          const data = res.data;
          if (data.code == 200) {
            if (!utils.isEmptyStr(data.data.replaceMedicines)) {
              this.changeDrugList = data.data.replaceMedicines;
              this.currentDrug = "";
              this.dialogDrugVisible = true;
            } else {
              this.$message({ message: "无可更换药品", type: "info" });
            }
          } else {
            this.$message({
              message:
                data.msg && data.msg.length > 0 ? data.msg : "无可更换药品",
              type: "error",
            });
          }
        },
        (err) => {
          console.log(err);
        }
      );
    },

    dialogConfirm() {
      if (utils.isEmptyStr(this.currentDrug)) {
        this.$message({ message: "请选择需要更换的药材", type: "info" });
        return;
      }
      this.dialogDrugVisible = false;
      const subIdx = this.isGrouped
        ? parseInt(this.groupedTabSubActive)
        : parseInt(this.tabActive);
      const params = {
        visitRecordId: this.visitRecordId,
        oldMedicineName:
          this.flag == 0 ? this.currentItem.key : this.currentItem.key1,
        replaceMedicineName: this.currentDrug,
        oldWeight:
          this.flag == 0 ? this.currentItem.value : this.currentItem.value1,
        replaceWeight:
          this.flag == 0 ? this.currentItem.value : this.currentItem.value1,
        number: subIdx + 1,
      };
      this.wjtHttp.post(
        this,
        this.config.replaceSubmitApi,
        params,
        false,
        (res) => {
          if (res.data.code == 200) this.refreshAfterReplace();
          else
            this.$message({
              message:
                res.data.msg && res.data.msg.length > 0
                  ? res.data.msg
                  : "更换药材失败",
              type: "error",
            });
        },
        (err) => {
          console.log(err);
        }
      );
    },

    drugWeightClick(item, flag) {
      this.currentItem = item;
      this.flag = flag;
      this.currentWeight = this.flag == 0 ? item.value : item.value1;
      this.dialogWeightVisible = true;
    },

    dialogWeightConfirm() {
      this.dialogWeightVisible = false;
      const subIdx = this.isGrouped
        ? parseInt(this.groupedTabSubActive)
        : parseInt(this.tabActive);
      const params = {
        visitRecordId: this.visitRecordId,
        oldMedicineName:
          this.flag == 0 ? this.currentItem.key : this.currentItem.key1,
        replaceMedicineName:
          this.flag == 0 ? this.currentItem.key : this.currentItem.key1,
        oldWeight:
          this.flag == 0 ? this.currentItem.value : this.currentItem.value1,
        replaceWeight: this.currentWeight,
        number: subIdx + 1,
      };
      this.wjtHttp.post(
        this,
        this.config.replaceSubmitApi,
        params,
        false,
        (res) => {
          if (res.data.code == 200) this.refreshAfterReplace();
          else
            this.$message({
              message:
                res.data.msg && res.data.msg.length > 0
                  ? res.data.msg
                  : "修改重量异常，请联系管理员",
              type: "error",
            });
        },
        (err) => {
          console.log(err);
        }
      );
    },

    refreshAfterReplace() {
      const api = this.config.replaceResultApi;
      this.wjtHttp.post(
        this,
        api,
        { visitRecordId: this.visitRecordId },
        false,
        (res) => {
          if (res.data.code == 200) {
            const result = res.data.data || res.data;
            // 兼容新旧数据格式
            this.prescriptions =
              result.prescriptionInfos && result.prescriptionInfos.length > 0
                ? result.prescriptionInfos
                : result.commonPrescription
                  ? [result.commonPrescription]
                  : [];
            this.generateTableData();
          } else
            this.$message({
              message:
                res.data.msg && res.data.msg.length > 0
                  ? res.data.msg
                  : "出方异常，请联系管理员",
              type: "error",
            });
        },
        (err) => {
          console.log(err);
        }
      );
    },

    numberChange(value) {
      this.currentItem.newValue = value;
    },

    parsePrescriptionToText() {
      const lines = [];

      // 分组接口（古中医组方）
      if (this.isGrouped) {
        if (this.consultInfo && this.consultInfo.name)
          lines.push(`患者姓名：${utils.blurName(this.consultInfo.name)}`);
        if (this.consultInfo && this.consultInfo.birthday) {
          let text = `患者生日：${this.consultInfo.birthday}`;
          // 出生时分 暂时隐藏（保留代码，恢复时去掉注释即可）
          // if (this.shouldShowTime(this.consultInfo.birthHour, this.consultInfo.birthMinute)) text += ` ${this.formatTimeValue(this.consultInfo.birthHour)}:${this.formatTimeValue(this.consultInfo.birthMinute)}`
          lines.push(text);
        }
        if (this.consultInfo && this.consultInfo.birthArea)
          lines.push(`患者出生地址：${this.consultInfo.birthArea}`);
        if (this.consultInfo && this.consultInfo.liveArea)
          lines.push(`常住地址：${this.consultInfo.liveArea}`);
        if (
          this.consultInfo &&
          (this.consultInfo.useDays ||
            (this.config.showGanZhi &&
              this.consultInfo.ganZhiPrescriptionHourRange))
        ) {
          const label = this.useDaysLabel;
          const value = this.useDaysValue;
          lines.push(`${label}：${value}`);
        }
        if (this.config.showSymptoms) {
          const symptoms = [];
          if (this.basicTableArray) {
            for (const item of this.basicTableArray) {
              if (item.key && item.value && item.value.length > 0)
                symptoms.push(`${item.key}：${item.value}`);
            }
          }
          if (this.shangjiaoTableArray) {
            for (const item of this.shangjiaoTableArray) {
              if (item.key && item.value)
                symptoms.push(`上焦-${item.key}：${item.value}`);
            }
          }
          if (this.zhongjiaoTableArray) {
            for (const item of this.zhongjiaoTableArray) {
              if (item.key && item.value)
                symptoms.push(`中焦-${item.key}：${item.value}`);
            }
          }
          if (this.xiajiaoTableArray) {
            for (const item of this.xiajiaoTableArray) {
              if (item.key && item.value)
                symptoms.push(`下焦-${item.key}：${item.value}`);
            }
          }
          if (symptoms.length > 0) {
            lines.push(`症状信息：`);
            symptoms.forEach((s) => lines.push(s));
          }
        }
        // 使用 currentGroup 判断
        const group = this.currentGroup;
        if (!group) {
          lines.push("药方内容：暂无处方数据");
          return lines.join("\n");
        }
        // 伤寒方
        if (group.isShangHan) {
          const shData = group.items[0];
          const content =
            shData &&
            shData.functions &&
            (shData.functions.prescription || shData.functions.shanghan);
          if (!content) {
            lines.push("药方内容：暂无伤寒方数据");
            return lines.join("\n");
          }
          lines.push("药方内容：");
          lines.push(content.brief || content.text || "暂无详细描述");
          if (this.groupedIllDay) lines.push(`发病时间：${this.groupedIllDay}`);
          return lines.join("\n");
        }
        // 其他分组
        const panes = group.items;
        if (!panes || panes.length === 0) {
          lines.push("药方内容：暂无处方数据");
          return lines.join("\n");
        }
        const idx = parseInt(this.groupedTabSubActive);
        if (isNaN(idx) || idx < 0 || idx >= panes.length) {
          lines.push("药方内容：暂无处方数据");
          return lines.join("\n");
        }
        const cp = panes[idx];
        if (!cp || !cp.options || cp.options.length === 0) {
          lines.push("药方内容：");
          lines.push("药材组成：暂无药材明细");
          lines.push(`总计：${cp.count || 0}味，${cp.totalWeight || 0}克`);
          lines.push(`使用期限：${cp.startDate || ""} 至 ${cp.endDate || ""}`);
          lines.push(`有效期：${cp.supplyDays || 0}天`);
          return lines.join("\n");
        }
        const herbs = [];
        for (const h of cp.options) {
          if (h.key && h.value) herbs.push(`${h.key}${h.value}克`);
          if (h.key1 && h.value1) herbs.push(`${h.key1}${h.value1}克`);
        }
        lines.push("药方内容：");
        lines.push(
          `药材组成：${herbs.length > 0 ? herbs.join("，") : "暂无药材明细"}`
        );
        lines.push(`总计：${cp.count}味，${cp.totalWeight}克`);
        lines.push(`使用期限：${cp.startDate} 至 ${cp.endDate}`);
        lines.push(`有效期：${cp.supplyDays}天`);
        return lines.join("\n");
      }

      // 以下为旧接口逻辑（自拟组方/通用方）
      if (this.activeTab != "健康指引信息" && this.activeTab != "针灸信息") {
        if (this.consultInfo && this.consultInfo.name)
          lines.push(`患者姓名：${utils.blurName(this.consultInfo.name)}`);
        if (this.consultInfo && this.consultInfo.birthday) {
          let text = `患者生日：${this.consultInfo.birthday}`;
          // 出生时分 暂时隐藏（保留代码，恢复时去掉注释即可）
          // if (this.shouldShowTime(this.consultInfo.birthHour, this.consultInfo.birthMinute)) text += ` ${this.formatTimeValue(this.consultInfo.birthHour)}:${this.formatTimeValue(this.consultInfo.birthMinute)}`
          lines.push(text);
        }
        if (this.consultInfo && this.consultInfo.birthArea)
          lines.push(`患者出生地址：${this.consultInfo.birthArea}`);
        if (this.consultInfo && this.consultInfo.liveArea)
          lines.push(`常住地址：${this.consultInfo.liveArea}`);
        if (
          this.consultInfo &&
          (this.consultInfo.useDays ||
            (this.config.showGanZhi &&
              this.consultInfo.ganZhiPrescriptionHourRange))
        ) {
          const label = this.useDaysLabel;
          const value = this.useDaysValue;
          lines.push(`${label}：${value}`);
        }
        if (this.config.showExtraBasicInfo) {
          if (this.consultInfo && this.consultInfo.useInstructions)
            lines.push(`使用方法：${this.consultInfo.useInstructions}`);
          if (this.consultInfo && this.consultInfo.taboo)
            lines.push(`禁忌：${this.consultInfo.taboo}`);
        }
        if (this.config.showDiagnosisInfo) {
          if (this.diagnosisType) lines.push(`诊断类型：${this.diagnosisType}`);
          if (this.diagnoseResult)
            lines.push(`诊断结果：${this.diagnoseResult}`);
          if (this.takeMedTime) lines.push(`服药时间：${this.takeMedTime}`);
        }
        if (this.config.showSymptoms) {
          const symptoms = [];
          if (this.basicTableArray) {
            for (const item of this.basicTableArray) {
              if (item.key && item.value && item.value.length > 0)
                symptoms.push(`${item.key}：${item.value}`);
            }
          }
          if (this.shangjiaoTableArray) {
            for (const item of this.shangjiaoTableArray) {
              if (item.key && item.value)
                symptoms.push(`上焦-${item.key}：${item.value}`);
            }
          }
          if (this.zhongjiaoTableArray) {
            for (const item of this.zhongjiaoTableArray) {
              if (item.key && item.value)
                symptoms.push(`中焦-${item.key}：${item.value}`);
            }
          }
          if (this.xiajiaoTableArray) {
            for (const item of this.xiajiaoTableArray) {
              if (item.key && item.value)
                symptoms.push(`下焦-${item.key}：${item.value}`);
            }
          }
          if (symptoms.length > 0) {
            lines.push(`症状信息：`);
            symptoms.forEach((s) => lines.push(s));
          }
        }
      }
      if (this.activeTab === "健康指引信息")
        return this.parseHealthPredictionToText(lines);
      if (this.activeTab === "针灸信息")
        return this.parseAcupunctureToText(lines);
      if (this.activeTab === "开方信息" && this.tabActive === "伤寒方") {
        if (
          !this.shangHanPrescriptionInfos ||
          !this.shangHanPrescriptionInfos.functions ||
          !this.shangHanPrescriptionInfos.functions.shanghan
        ) {
          lines.push("药方内容：暂无伤寒方数据");
          return lines.join("\n");
        }
        lines.push("药方内容：");
        lines.push(
          this.shangHanPrescriptionInfos.functions.shanghan.brief ||
          "暂无详细描述"
        );
        if (this.illDay) lines.push(`发病时间：${this.illDay}`);
        return lines.join("\n");
      }
      const source =
        this.activeTab === "通用方信息"
          ? this.tablePrescription2
          : this.tablePrescription;
      const idx =
        this.activeTab === "通用方信息"
          ? parseInt(this.tabActive2)
          : parseInt(this.tabActive);
      if (
        !source ||
        source.length === 0 ||
        isNaN(idx) ||
        idx < 0 ||
        idx >= source.length
      ) {
        lines.push("药方内容：暂无处方数据");
        return lines.join("\n");
      }
      const cp = source[idx];
      if (!cp || !cp.options || cp.options.length === 0) {
        lines.push("药方内容：");
        lines.push("药材组成：暂无药材明细");
        lines.push(`总计：${cp.count || 0}味，${cp.totalWeight || 0}克`);
        lines.push(`使用期限：${cp.startDate || ""} 至 ${cp.endDate || ""}`);
        lines.push(`有效期：${cp.supplyDays || 0}天`);
        return lines.join("\n");
      }
      const herbs = [];
      for (const h of cp.options) {
        if (h.key && h.value) herbs.push(`${h.key}${h.value}克`);
        if (h.key1 && h.value1) herbs.push(`${h.key1}${h.value1}克`);
      }
      lines.push("药方内容：");
      lines.push(
        `药材组成：${herbs.length > 0 ? herbs.join("，") : "暂无药材明细"}`
      );
      lines.push(`总计：${cp.count}味，${cp.totalWeight}克`);
      lines.push(`使用期限：${cp.startDate} 至 ${cp.endDate}`);
      lines.push(`有效期：${cp.supplyDays}天`);
      return lines.join("\n");
    },

    parseHealthPredictionToText(baseLines) {
      const lines = [...baseLines];
      if (!this.resultsJKYC || this.resultsJKYC.length === 0) {
        lines.push("健康指引信息：暂无数据");
        return lines.join("\n");
      }
      lines.push("健康指引信息：");
      this.resultsJKYC.forEach((item) => {
        if (item && item.trim()) lines.push(item);
      });
      return lines.join("\n");
    },

    parseAcupunctureToText(baseLines) {
      const lines = [...baseLines];
      if (!this.resultsZJYC || this.resultsZJYC.length === 0) {
        lines.push("针灸信息：暂无数据");
        return lines.join("\n");
      }
      lines.push("针灸信息：");
      this.resultsZJYC.forEach((item) => {
        if (item && item.trim()) lines.push(item);
      });
      return lines.join("\n");
    },

    sendPrescriptionToAi() {
      const text = this.parsePrescriptionToText();
      if (
        text.includes("暂无") ||
        text.includes("异常") ||
        text.includes("无效")
      ) {
        this.$message.warning(text);
        return;
      }
      let sourceType = "prescription";
      let prescriptionType = this.activeTab;
      if (this.isGrouped) {
        sourceType = "prescription";
        prescriptionType = this.activeGroupedTab;
      } else if (this.activeTab === "健康指引信息") {
        sourceType = "health_prediction";
      } else if (this.activeTab === "针灸信息") {
        sourceType = "acupuncture";
      } else if (this.activeTab === "通用方信息") {
        sourceType = "general_prescription";
      }
      EventBus.$emit("ai-input-text", {
        text,
        source: sourceType,
        prescriptionType,
        patientName:
          this.consultInfo && this.consultInfo.name
            ? utils.blurName(this.consultInfo.name)
            : "",
      });
      this.$message.success("处方已填充到AI对话框，可以开始咨询");
    },
  },
};
</script>

<style scoped>
/* ============================================================
   PrescriptionPage — 组方方案（青绿主题 / 参考设计图）
   ============================================================ */
.page-wrapper {
  min-height: 100vh;
  background: #f5f7f2;
  font-family: "思源黑体", "Source Han Sans SC", "Noto Sans SC", system-ui, sans-serif;
  color: #304744;
  padding-bottom: 40px;
}

.page-container {
  max-width: 1220px;
  margin: 0 auto;
  padding: 32px 24px 40px;
}

/* ========== 面包屑 ========== */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  margin-bottom: 18px;
}

.bc-link {
  color: #527f77;
  cursor: pointer;
}

.bc-link:hover {
  color: #009798;
}

.bc-sep {
  color: #b6c6c1;
}

.bc-current {
  color: #304744;
  font-weight: 600;
}

/* ========== Hero ========== */
.hero-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.hero-brand {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 600;
  color: #009798;
  letter-spacing: 1px;
}

.hero-title {
  margin: 0 0 12px;
  font-size: 40px;
  font-weight: 700;
  color: #304744;
  letter-spacing: 1px;
  line-height: 1.2;
}

.hero-subtitle {
  margin: 0;
  font-size: 17px;
  color: #527f77;
}

.hero-back {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border: 1px solid #cfdcd8;
  border-radius: 12px;
  background: #fff;
  color: #304744;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hero-back:hover {
  border-color: #009798;
  color: #009798;
}

.hero-back-arrow {
  color: #009798;
  font-weight: 700;
}

/* ========== 提示条 ========== */
.notice-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 22px;
  margin-bottom: 26px;
  border: 1px solid #f2e4c4;
  border-radius: 14px;
  background: #fdf7e9;
}

.notice-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid #c9a24a;
  color: #c9a24a;
  font-size: 16px;
  font-weight: 700;
}

.notice-text {
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: #8a6d3b;
}

/* ========== 双栏布局 ========== */
.layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 28px;
  align-items: start;
}

.side-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 16px;
}

/* ========== 左侧健康档案卡 ========== */
.record-card {
  background: #fff;
  border: 1px solid #e9efec;
  border-radius: 20px;
  padding: 24px 22px;
  box-shadow: 0 10px 30px rgba(23, 61, 59, 0.05);
}

.record-eyebrow {
  margin: 0 0 18px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #a9bcb6;
}

.record-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef2f0;
  margin-bottom: 16px;
}

.record-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #d9efeb;
  color: #009798;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.record-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.record-name {
  font-size: 22px;
  font-weight: 700;
  color: #1f3a34;
}

.record-sub {
  font-size: 16px;
  color: #7f9994;
}

.record-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.r-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  font-size: 16px;
}

.r-k {
  color: #9aa8a3;
  flex-shrink: 0;
}

.r-v {
  color: #304744;
  text-align: right;
  word-break: break-all;
}

/* 症状与就诊记录（折叠） */
.record-symptoms {
  margin-top: 20px;
  border-top: 1px solid #eef2f0;
  padding-top: 14px;
}

.rs-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  color: #304744;
}

.rs-caret {
  display: inline-block;
  color: #009798;
  font-size: 16px;
  transition: transform 0.2s ease;
  transform: rotate(-90deg);
}

.rs-caret.open {
  transform: rotate(0deg);
}

.rs-body {
  padding-top: 10px;
}

.rs-group+.rs-group {
  margin-top: 18px;
}

.rs-group-title {
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #009798;
}

.rs-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rs-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  font-size: 16px;
  line-height: 1.5;
}

.rs-k {
  color: #9aa8a3;
  flex-shrink: 0;
}

.rs-v {
  color: #304744;
  text-align: right;
  word-break: break-all;
}

/* 三焦辨证分组折叠 */
.sanjiao-block {
  border: 1px solid #eef2f0;
  border-radius: 12px;
  background: #fbfdfc;
  padding: 4px 12px;
  margin-top: 10px;
}

.sanjiao-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 16px;
  color: #304744;
}

.sj-caret {
  display: inline-block;
  color: #527f77;
  font-size: 16px;
  transition: transform 0.2s ease;
  transform: rotate(-90deg);
}

.sj-caret.open {
  transform: rotate(0deg);
}

.sj-name {
  font-weight: 700;
}

.sj-count {
  margin-left: auto;
  font-size: 16px;
  color: #9aa8a3;
}

.sanjiao-body {
  padding: 2px 0 12px;
}

.record-note {
  margin: 20px 0 0;
  padding-top: 14px;
  border-top: 1px solid #eef2f0;
  font-size: 16px;
  line-height: 1.7;
  color: #9aa8a3;
}

/* ========== 右侧组方区 ========== */
.main-right {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.panel-card {
  background: #fff;
  border: 1px solid #e9efec;
  border-radius: 22px;
  padding: 28px 32px 30px;
  box-shadow: 0 10px 30px rgba(23, 61, 59, 0.06);
}

.panel-eyebrow {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #a9bcb6;
}

.panel-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.panel-title {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  color: #304744;
}

.panel-note {
  font-size: 16px;
  color: #9aa8a3;
}

.panel-badge {
  margin-left: auto;
  flex-shrink: 0;
  padding: 6px 16px;
  border-radius: 10px;
  background: #e6f4f1;
  color: #009798;
  font-size: 16px;
  font-weight: 600;
}

/* 方案类型卡片 */
.type-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.type-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  padding: 18px 18px 20px;
  border: 1px solid #e2eae7;
  border-radius: 16px;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;
}

.type-card:hover {
  border-color: #b7ddd8;
  transform: translateY(-2px);
}

.type-card-active {
  border-color: #009798;
  background: #eef8f6;
  box-shadow: 0 10px 24px rgba(0, 151, 152, 0.12);
}

.type-idx {
  font-size: 16px;
  font-weight: 700;
  color: #b6c6c1;
}

.type-card-active .type-idx {
  color: #009798;
}

.type-name {
  font-size: 20px;
  font-weight: 700;
  color: #304744;
}

.type-desc {
  font-size: 16px;
  color: #9aa8a3;
}

/* 子 Tab（处方多标签） */
.sub-tabs {
  width: 100%;
}

.sub-tabs>>>.el-tabs__header {
  margin-bottom: 20px;
}

.sub-tabs>>>.el-tabs__nav-wrap::after {
  background-color: #edf2ee;
}

.sub-tabs>>>.el-tabs__item {
  font-size: 17px;
  color: #7f9994;
  height: 42px;
  line-height: 42px;
}

.sub-tabs>>>.el-tabs__item.is-active {
  color: #009798;
  font-weight: 600;
}

.sub-tabs>>>.el-tabs__active-bar {
  background-color: #009798;
}

/* 空状态 */
.panel-empty {
  margin: 0;
  padding: 26px 0;
  text-align: center;
  font-size: 16px;
  color: #a7b8b4;
}

/* 针灸 / 健康指引信息块 */
.info-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 22px 26px;
  border: 1px solid #e6f0ee;
  border-radius: 16px;
  background: #f8fcfb;
}

.info-block p {
  margin: 0;
  font-size: 17px;
  line-height: 1.8;
  color: #2f6f74;
}

/* 伤寒方 */
.shanghan-info {
  text-align: left;
  font-size: 17px;
  padding: 4px 2px;
  font-family: "思源黑体", "Source Han Sans SC", "Noto Sans SC", system-ui, sans-serif;
}

.shanghan-item {
  margin-bottom: 12px;
  color: #304744;
  font-family: "思源黑体", "Source Han Sans SC", "Noto Sans SC", system-ui, sans-serif;
}

.shanghan-brief {
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 17px;
  line-height: 1.8;
  color: #4a5f5b;
  margin: 0;
  font-family: "思源黑体", "Source Han Sans SC", "Noto Sans SC", system-ui, sans-serif;
}

/* ========== 底部审核条 ========== */
.review-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 28px;
  padding: 22px 28px;
  border: 1px solid #dcebe7;
  border-radius: 20px;
  background: linear-gradient(105deg, #eaf6f3 0%, #f3faf8 100%);
}

.review-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #304744;
}

.review-desc {
  margin: 0;
  font-size: 16px;
  color: #527f77;
}

.review-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.btn-ghost,
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 26px;
  border-radius: 9999px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-ghost {
  border: 1px solid #cfdcd8;
  background: #fff;
  color: #304744;
}

.btn-ghost:hover {
  border-color: #009798;
  color: #009798;
}

.btn-ghost:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  border: 1px solid #009798;
  background: #009798;
  color: #fff;
  box-shadow: 0 10px 22px rgba(0, 151, 152, 0.22);
}

.btn-primary:hover {
  background: #007e7f;
  border-color: #007e7f;
  transform: translateY(-2px);
}

.btn-arrow {
  font-size: 16px;
}

/* ========== 返回我的服务 ========== */
.back-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.back-service {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border: 1px solid #cfdcd8;
  border-radius: 12px;
  background: #fff;
  color: #304744;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-service:hover {
  border-color: #009798;
  color: #009798;
}

/* ========== 页脚 ========== */
.page-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 40px;
  padding-top: 22px;
  border-top: 1px solid #e6edea;
  font-size: 16px;
  color: #9aa8a3;
}

/* ========== 弹窗 ========== */
.dialog-radio-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.dialog-radio {
  margin: 0;
  width: 100%;
}

.dialog-weight-row {
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: center;
}

.weight-drug-name {
  font-size: 16px;
  font-weight: 500;
}

/* ========== 响应式 ========== */
@media (max-width: 1024px) {
  .layout {
    grid-template-columns: 1fr;
  }

  .side-col {
    position: static;
  }

  .type-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .review-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .review-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 20px 16px 40px;
  }

  .hero-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-title {
    font-size: 28px;
  }

  .type-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .panel-card {
    padding: 20px 18px 22px;
    border-radius: 18px;
  }

  .panel-title {
    font-size: 22px;
  }

  .page-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .review-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-ghost,
  .btn-primary {
    width: 100%;
  }
}
</style>
